import { ComponentProps, useMemo, useRef } from 'react'
import { useResizeDetector } from 'react-resize-detector'
import { Bar, Tooltip, XAxis, YAxis } from 'recharts'

import { useIsDark } from '../../../hooks/useIsDark'
import { RechartsTooltip } from './RechartsTooltip'
import {
  BAR_CORNER_RADIUS,
  SNAPSHOT_AXIS_TICK_X,
  SNAPSHOT_AXIS_TICK_Y,
  SNAPSHOT_Y_AXIS_WIDTH,
} from './chart.constants'
import { ChartData, StackedChartData } from './chart.types'
import { useChartColors } from './useChartColors'

/** Fixes Recharts overflow issue on the y axis. */
const Y_Axis_OVERFLOW_FIX_SPACING = 5

/** Round up to a "nice" axis top (e.g. 18 -> 20). Exported for horizontal chart. */
export const niceCeil = (value: number): number => {
  if (value <= 0) return 0
  const magnitude = Math.pow(10, Math.floor(Math.log10(value)))
  const normalized = value / magnitude
  const step =
    normalized <= 1 ? 0.2 : normalized <= 2 ? 0.5 : normalized <= 5 ? 1 : 2
  return Math.ceil(value / (step * magnitude)) * step * magnitude
}

/** Round down to a "nice" axis bottom (e.g. -7 -> -10). */
const niceFloor = (value: number): number => {
  if (value >= 0) return 0
  const magnitude = Math.pow(10, Math.floor(Math.log10(-value)))
  const normalized = -value / magnitude
  const step =
    normalized <= 1 ? 0.2 : normalized <= 2 ? 0.5 : normalized <= 5 ? 1 : 2
  return -Math.ceil(-value / (step * magnitude)) * step * magnitude
}

/** Round to a "nice" step for axis ticks (e.g. 6.25 -> 5, 12 -> 10). */
const getNiceStep = (idealStep: number): number => {
  if (idealStep <= 0) return 1
  const magnitude = Math.pow(10, Math.floor(Math.log10(idealStep)))
  const normalized = idealStep / magnitude
  const niceNormalized =
    normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10
  return niceNormalized * magnitude
}

/** Generate evenly spaced, round axis tick values (e.g. 0, 5, 10, 15, 20, 25). Exported for horizontal chart. */
export const getNiceTicks = (
  min: number,
  max: number,
  tickCount: number,
): number[] => {
  if (min >= max) return [min]
  const range = max - min
  const idealStep = range / Math.max(1, tickCount - 1)
  const step = getNiceStep(idealStep)
  const ticks: number[] = []
  const start = min === 0 ? 0 : Math.ceil(min / step) * step
  for (let t = start; t < max; t += step) {
    if (t >= min) ticks.push(Number(t.toPrecision(10)))
  }
  ticks.push(max)
  return ticks
}

const sumSeriesRow = (
  point: Record<string, unknown>,
  series: { dataKey: string }[],
): number => {
  let sum = 0
  for (const s of series) {
    const v = point[s.dataKey]
    if (typeof v === 'number' && Number.isFinite(v)) sum += v
  }
  return sum
}

export type UseCustomRechartsOptions = {
  chartData: ChartData | StackedChartData
  /** Number of Y-axis ticks. Default 5. Used to generate nice round tick values (e.g. 0, 5, 10, 15, 20). */
  yAxisTickCount?: number
  /** When set, fixes the Y-axis ceiling instead of nicing the data max (Figma Port-to-Port bar/MoM: 70). */
  yAxisMax?: number
  series?: { dataKey: string }[]
  /** Stacked bars: Y domain from per-row series sums. */
  stacked?: boolean
  /** Homepage snapshot dashboard charts (Figma tick typography). */
  variant?: 'default' | 'snapshot'
}

export const useCustomRecharts = ({
  chartData,
  yAxisTickCount = 5,
  yAxisMax,
  series,
  stacked = false,
  variant = 'default',
}: UseCustomRechartsOptions) => {
  const isSnapshot = variant === 'snapshot'
  const isDark = useIsDark()
  const chartColors = useChartColors()
  const snapshotXAxisTick = isDark
    ? SNAPSHOT_AXIS_TICK_X
    : {
        ...SNAPSHOT_AXIS_TICK_X,
        fill: chartColors.rechartsAxisTick.fill,
      }
  const snapshotYAxisTick = isDark
    ? SNAPSHOT_AXIS_TICK_Y
    : {
        ...SNAPSHOT_AXIS_TICK_Y,
        fill: chartColors.rechartsAxisTick.fill,
      }
  const xAxisTick = isSnapshot
    ? snapshotXAxisTick
    : chartColors.rechartsAxisTick
  const yAxisTick = isSnapshot
    ? snapshotYAxisTick
    : chartColors.rechartsAxisTick
  const chartWrapperRef = useRef<HTMLDivElement>(null)
  const { width: wrapperWidth } = useResizeDetector({
    targetRef: chartWrapperRef,
  })

  /** This is required with Recharts to properly set the size of the x and y axis */
  const { yAxisWidth, xAxisHeight } = useMemo(() => {
    const yWidth = chartWrapperRef.current
      ?.querySelector<SVGElement>('.recharts-yAxis')
      ?.getClientRects()[0]?.width

    const xHeight = chartWrapperRef.current
      ?.querySelector<SVGElement>('.recharts-xAxis')
      ?.getClientRects()[0]?.height

    return {
      yAxisWidth: yWidth
        ? Math.ceil(yWidth) + Y_Axis_OVERFLOW_FIX_SPACING
        : undefined,
      xAxisHeight: xHeight ? Math.ceil(xHeight) : undefined,
    }
  }, [wrapperWidth])

  const chartValues = chartData?.chartValues ?? []

  const values = useMemo(() => {
    const collected: number[] = []
    for (const point of chartValues as any[]) {
      if (point.chartValue) {
        collected.push(point.chartValue.value)
      } else if (stacked && series && series.length > 1) {
        collected.push(sumSeriesRow(point, series))
      } else if (series && series.length > 0) {
        for (const s of series) {
          const v = point[s.dataKey]
          if (typeof v === 'number') collected.push(v)
        }
      } else if ((chartData as ChartData).valueKey) {
        const vk = (chartData as ChartData).valueKey!
        const v = point[vk]
        if (typeof v === 'number') collected.push(v)
      } else {
        for (const k of Object.keys(point)) {
          if (k === 'key') continue
          const v = (point as Record<string, unknown>)[k]
          if (typeof v === 'number') collected.push(v)
        }
      }
    }
    return collected
  }, [chartValues, series, chartData, stacked])

  const maxValue = values.length ? Math.max(...values) : 0
  const minValue = values.length ? Math.min(...values) : 0
  const chartTopY =
    yAxisMax != null && yAxisMax > 0 ? yAxisMax : niceCeil(maxValue)
  const chartBottomY = minValue < 0 ? niceFloor(minValue) : 0
  const yAxisTicks = useMemo(
    () => getNiceTicks(chartBottomY, chartTopY, yAxisTickCount),
    [chartBottomY, chartTopY, yAxisTickCount],
  )

  return {
    chartWrapperRef,
    XAxis: (props: ComponentProps<typeof XAxis> = {}) => (
      // Spread `height` only when defined: React 19's JSX runtime no longer
      // applies `defaultProps`, so passing `height={undefined}` overrides
      // Recharts' default (30) with undefined and breaks axis layout (NaN).
      <XAxis
        axisLine={false}
        tickLine={false}
        interval={0}
        dy={isSnapshot ? 0 : 3}
        angle={isSnapshot ? -30 : -45}
        textAnchor="end"
        tick={xAxisTick}
        tickFormatter={(value: string) => {
          if (value.length > 13) return value.slice(0, 13) + '...'

          return value
        }}
        {...(xAxisHeight !== undefined && { height: xAxisHeight })}
        {...props}
      ></XAxis>
    ),
    YAxis: (props: ComponentProps<typeof YAxis> = {}) => (
      // Spread `width` only when defined for the same reason as XAxis above.
      <YAxis
        axisLine={false}
        tickLine={false}
        ticks={yAxisTicks}
        domain={[chartBottomY, chartTopY]}
        tick={yAxisTick}
        {...(isSnapshot
          ? {
              width: props.width ?? SNAPSHOT_Y_AXIS_WIDTH,
              tickMargin: props.tickMargin ?? 10,
            }
          : yAxisWidth !== undefined
            ? { width: yAxisWidth }
            : {})}
        {...props}
      />
    ),
    BarBackground: (props: any = {}) => (
      <Bar
        {...props}
        className="recharts-bar-background-rectangle"
        radius={BAR_CORNER_RADIUS}
        tooltipType="none"
        background={{
          fill: chartColors.barBackground,
          radius: BAR_CORNER_RADIUS,
        }}
      />
    ),
    ToolTip: (props: any = {}) => (
      <Tooltip content={<RechartsTooltip />} cursor={false} {...props} />
    ),
  }
}
